// =============================================================================
// ApiClient — single entry point for all REST calls.
//
// Behaviour:
//   - Bearer JWT injection from a TokenProvider hook
//   - Automatic 401 → refresh token → retry once
//   - Idempotency-Key header on POSTs that opt in
//   - 429 backoff using Retry-After when present
//   - 409 idempotency_in_flight short retry
//   - JSON in/out; bigint fields are strings on the wire
//   - Typed errors with parsed `error` code
//
// See docs/API.md & docs/CLIENT.md.
// =============================================================================

export interface ApiError extends Error {
  status: number;
  code: string;
  body?: unknown;
}

export interface TokenProvider {
  /** Current access token, or null when unauthenticated. */
  getToken: () => string | null;
  /** Try to refresh; resolve with new token, or null if refresh failed. */
  refresh: () => Promise<string | null>;
  /** Called when refresh fails (force re-login). */
  onAuthFailed?: () => void;
}

export interface ApiClientOptions {
  baseUrl: string;
  tokenProvider?: TokenProvider;
  /** ms — abort if a single attempt takes longer. Default 15000. */
  timeoutMs?: number;
  /** Maximum retries for transient errors (429, 409 in-flight). Default 2. */
  maxTransientRetries?: number;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  /** Search params for GETs (other methods can also use it). */
  query?: Record<string, string | number | boolean | undefined>;
  /** When set, sent as Idempotency-Key header. Auto-generated for opted-in
   *  endpoints if you call `apiIdempotent()`. */
  idempotencyKey?: string;
  /** Skip Bearer injection (only auth endpoints). */
  noAuth?: boolean;
  /** Add request headers. */
  headers?: Record<string, string>;
  /** Override timeout. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_MAX_RETRIES = 2;

export class ApiClient {
  baseUrl: string;
  tokenProvider: TokenProvider | undefined;
  private timeoutMs: number;
  private maxTransientRetries: number;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.tokenProvider = opts.tokenProvider;
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.maxTransientRetries = opts.maxTransientRetries ?? DEFAULT_MAX_RETRIES;
  }

  setTokenProvider(p: TokenProvider): void {
    this.tokenProvider = p;
  }

  // ---- typed sugar -----------------------------------------------------

  get<T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "GET" });
  }

  post<T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "POST", body });
  }

  /**
   * POST with auto-generated `Idempotency-Key`. Use for endpoints listed in
   * docs/API.md (battle/start, gacha/pull, refine, market/list, market/buy).
   */
  postIdempotent<T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, {
      ...opts,
      method: "POST",
      body,
      idempotencyKey: opts.idempotencyKey ?? randomKey(),
    });
  }

  // ---- core ------------------------------------------------------------

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path, opts.query);
    const timeoutMs = opts.timeoutMs ?? this.timeoutMs;
    const maxRetries = this.maxTransientRetries;
    let attempt = 0;
    let triedRefresh = false;

    while (true) {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const headers = await this.buildHeaders(opts);
        const init: RequestInit = {
          method: opts.method ?? "GET",
          headers,
          signal: controller.signal,
        };
        if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

        const res = await fetch(url, init);
        clearTimeout(t);

        if (res.ok) {
          return (await parseBody(res)) as T;
        }

        // 401 → try refresh once and replay
        if (res.status === 401 && !opts.noAuth && !triedRefresh && this.tokenProvider) {
          const newTok = await this.tokenProvider.refresh().catch(() => null);
          triedRefresh = true;
          if (newTok) continue;
          this.tokenProvider.onAuthFailed?.();
          throw await readError(res);
        }

        // 429 → respect Retry-After (or short backoff)
        if (res.status === 429 && attempt < maxRetries) {
          const retryAfter = parseRetryAfter(res.headers.get("retry-after"));
          await sleep(retryAfter ?? 1000 * (attempt + 1));
          attempt++;
          continue;
        }

        // 409 idempotency_in_flight → wait briefly and retry
        if (res.status === 409 && attempt < maxRetries) {
          const err = await peekError(res);
          if (err === "idempotency_in_flight") {
            await sleep(800 + 400 * attempt);
            attempt++;
            continue;
          }
          throw await readError(res, err);
        }

        throw await readError(res);
      } catch (err) {
        clearTimeout(t);
        if ((err as { name?: string }).name === "AbortError") {
          throw makeApiError(0, "timeout", `request timed out: ${path}`);
        }
        throw err;
      }
    }
  }

  // ---- internal --------------------------------------------------------

  private buildUrl(path: string, query?: RequestOptions["query"]): string {
    const url = path.startsWith("http") ? path : `${this.baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      params.append(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
  }

  private async buildHeaders(opts: RequestOptions): Promise<Record<string, string>> {
    const h: Record<string, string> = {
      accept: "application/json",
      ...(opts.headers ?? {}),
    };
    if (opts.body !== undefined) h["content-type"] = "application/json";
    if (opts.idempotencyKey) h["idempotency-key"] = opts.idempotencyKey;
    if (!opts.noAuth && this.tokenProvider) {
      const tok = this.tokenProvider.getToken();
      if (tok) h.authorization = `Bearer ${tok}`;
    }
    return h;
  }
}

// ---- helpers ----------------------------------------------------------

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

async function readError(res: Response, prefetched?: string): Promise<ApiError> {
  let body: unknown = undefined;
  let code = prefetched ?? "http_" + res.status;
  try {
    body = await parseBody(res);
    if (body && typeof body === "object" && "error" in (body as Record<string, unknown>)) {
      code = String((body as { error: unknown }).error);
    }
  } catch {
    // ignore
  }
  return makeApiError(res.status, code, `${res.status} ${res.statusText} (${code})`, body);
}

async function peekError(res: Response): Promise<string | undefined> {
  // Read body once; clone if we need to read again for the actual error path.
  try {
    const cloned = res.clone();
    const data = await cloned.json();
    if (data && typeof data === "object" && "error" in data) return String((data as { error: unknown }).error);
  } catch {
    // ignore
  }
  return undefined;
}

function makeApiError(status: number, code: string, message: string, body?: unknown): ApiError {
  const err = new Error(message) as ApiError;
  err.name = "ApiError";
  err.status = status;
  err.code = code;
  if (body !== undefined) err.body = body;
  return err;
}

function parseRetryAfter(h: string | null): number | null {
  if (!h) return null;
  const sec = Number(h);
  if (!Number.isNaN(sec)) return sec * 1000;
  const ms = Date.parse(h) - Date.now();
  return ms > 0 ? ms : null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function randomKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
